/*
 * Copyright 2026 Sony Corporation
 */

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Link,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';

import { AssetGrid } from './AssetGrid';
import { CollapsedRail } from '../panels/CollapsedRail';
import { LIGHT_TYPES, PRIMITIVE_TYPES } from '../../scene-doc/schema';
import { Outliner } from '../scene-page/Outliner';
import { PRIMITIVE_ICONS } from './primitive-icons';
import { packAsset, packSketchfab } from '../../services/asset-drag';
import { useLibrary } from '../../hooks/use-library';
import { useSketchfab } from '../../hooks/use-sketchfab';
import { useSketchfabAuth } from '../../hooks/use-sketchfab-auth';
import type { AssetType } from '../../services/polyhaven';
import type { AssetSource } from '@ips-device-verse/twin-service/schema/scene-doc';
import type { LightType, PrimitiveType, SceneDoc, SceneNode, SceneOp } from '../../scene-doc/schema';

export type LibraryTab = AssetType | 'primitives' | 'lighting' | 'mine';
export type PanelMode = 'library' | 'outliner';

// Singular: each one names what you are about to add, not a collection you are
// browsing. Primitives sit beside models because both are geometry.
const TABS: { id: LibraryTab; label: string }[] = [
  { id: 'models', label: 'Model' },
  { id: 'primitives', label: 'Primitive' },
  { id: 'hdris', label: 'HDRI' },
  { id: 'textures', label: 'Texture' },
  { id: 'lighting', label: 'Lighting' },
  { id: 'mine', label: 'My Library' },
];

const LABELS: Record<PrimitiveType | LightType, string> = {
  box: 'Box',
  sphere: 'Sphere',
  cylinder: 'Cylinder',
  plane: 'Plane',
  ground: 'Ground',
  directional: 'Directional',
  point: 'Point',
  spot: 'Spot',
};

const isAssetType = (tab: LibraryTab): tab is AssetType => tab === 'models' || tab === 'hdris' || tab === 'textures';

export interface LibraryPanelProps {
  doc: SceneDoc;
  selection: readonly string[];
  mode: PanelMode;
  onModeChange: (mode: PanelMode) => void;
  tab: LibraryTab;
  onTabChange: (tab: LibraryTab) => void;
  onSelect: (ids: readonly string[]) => void;
  dispatch: (ops: readonly SceneOp[]) => void;
}

/**
 * The kinds of thing a camera scene is usually built from.
 *
 * Searches, not filters. Poly Haven's categories are its own taxonomy and
 * Sketchfab has none at all, so nothing could filter both; what does work
 * across two catalogues is the query itself. It is also the only way to reach
 * the half of this list Poly Haven does not stock — it has no people.
 */
const MODEL_SEARCHES = ['human', 'car', 'studio', 'house'] as const;

/**
 * Two ranked lists into one, taking from each in turn.
 *
 * Not concatenation: the grid only builds the first page, and Poly Haven's
 * search alone can fill it, so appending Sketchfab would put it permanently
 * out of sight. Alternating keeps each source's own ranking and still shows
 * both in the first rows.
 */
const interleave = <T,>(left: readonly T[], right: readonly T[]): T[] => {
  const merged: T[] = [];
  for (let i = 0; i < Math.max(left.length, right.length); i++) {
    if (i < left.length) {
      merged.push(left[i]);
    }
    if (i < right.length) {
      merged.push(right[i]);
    }
  }
  return merged;
};

/**
 * A tile id, which has to say where the model came from.
 *
 * The two catalogues have separate id spaces — a Poly Haven slug and a
 * Sketchfab uid could collide — and picking one has to dispatch the right
 * source, so the source travels with the id rather than being inferred later.
 */
const tileId = (source: AssetSource, id: string): string => `${source}:${id}`;

/** The live catalogues, and what picking from one means for this tab. */
const CatalogTab = ({
  type,
  target,
  dispatch,
}: {
  type: AssetType;
  /** What a texture would land on. A texture needs somewhere to go; a model does not. */
  target?: SceneNode;
  dispatch: (ops: readonly SceneOp[]) => void;
}) => {
  const { assets, categories, category, setCategory, query, setQuery, loading, error } = useLibrary(type);

  // Models come from both sources; an HDRI or a texture is Poly Haven's alone.
  const wantsSketchfab = type === 'models';
  const sketchfab = useSketchfab(wantsSketchfab ? query : '');
  const { session } = useSketchfabAuth();

  const bySlug = useMemo(() => new Map(assets.map((asset) => [asset.slug, asset])), [assets]);
  const byUid = useMemo(() => new Map(sketchfab.assets.map((asset) => [asset.uid, asset])), [sketchfab.assets]);

  const pick = (id: string) => {
    const [source, key] = [id.slice(0, id.indexOf(':')), id.slice(id.indexOf(':') + 1)];

    if (source === 'sketchfab') {
      const asset = byUid.get(key);
      if (!asset) {
        return;
      }
      // Importing is the user's own account doing it (devplan §5.8), so without
      // one the useful thing to do with a catalogue is open it.
      if (session) {
        dispatch([{ op: 'add_asset', source: 'sketchfab', slug: asset.uid, name: asset.name }]);
      } else {
        window.open(asset.viewer_url, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    const asset = bySlug.get(key);
    if (!asset) {
      return;
    }
    if (type === 'hdris') {
      dispatch([{ op: 'set_environment', slug: asset.slug, pinned_hash: asset.filesHash }]);
    } else if (type === 'models') {
      dispatch([
        { op: 'add_asset', source: 'polyhaven', slug: asset.slug, name: asset.name, pinned_hash: asset.filesHash },
      ]);
    } else if (target?.kind === 'primitive') {
      dispatch([{ op: 'set_material', node_ids: [target.id], slug: asset.slug, pinned_hash: asset.filesHash }]);
    }
  };

  const pack = (id: string): string | undefined => {
    const [source, key] = [id.slice(0, id.indexOf(':')), id.slice(id.indexOf(':') + 1)];

    if (source === 'sketchfab') {
      const asset = session ? byUid.get(key) : undefined;
      return asset ? packSketchfab(asset.uid, asset.name) : undefined;
    }
    const asset = bySlug.get(key);
    return asset ? packAsset(type === 'models' ? 'model' : 'texture', asset) : undefined;
  };

  // Alternated rather than appended, so a Poly Haven search that fills the
  // page cannot hide Sketchfab entirely.
  const tiles = interleave(
    assets.map((asset) => ({
      id: tileId('polyhaven', asset.slug),
      name: asset.name,
      thumbnailUrl: asset.thumbnailUrl,
    })),
    wantsSketchfab
      ? sketchfab.assets.map((asset) => ({
          id: tileId('sketchfab', asset.uid),
          name: asset.name,
          thumbnailUrl: asset.thumbnail_url,
          title: [asset.name, asset.author, asset.license].filter(Boolean).join(' — '),
        }))
      : []
  );

  // Clicking still needs somewhere to put it; dragging does not, which is why
  // the grid stays live either way.
  const blocked = type === 'textures' && target?.kind !== 'primitive';

  return (
    <>
      <Box className="px-3 pt-3">
        <TextField
          size="small"
          fullWidth
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={`Search ${type}`}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      <Box className="flex flex-wrap gap-1 px-3 py-2">
        {wantsSketchfab ? (
          <>
            <Chip label="All" size="small" color={query === '' ? 'primary' : 'default'} onClick={() => setQuery('')} />
            {MODEL_SEARCHES.map((term) => (
              <Chip
                key={term}
                label={term}
                size="small"
                color={query === term ? 'primary' : 'default'}
                onClick={() => setQuery(query === term ? '' : term)}
              />
            ))}
          </>
        ) : (
          <>
            <Chip
              label="All"
              size="small"
              color={category === undefined ? 'primary' : 'default'}
              onClick={() => setCategory(undefined)}
            />
            {categories.map((name) => (
              <Chip
                key={name}
                label={name}
                size="small"
                color={category === name ? 'primary' : 'default'}
                onClick={() => setCategory(category === name ? undefined : name)}
              />
            ))}
          </>
        )}
      </Box>

      {type === 'textures' && (
        <Box className="flex items-center justify-between gap-2 px-3 pb-2">
          <Typography variant="caption" color={blocked ? 'warning.main' : 'text.secondary'}>
            {target === undefined
              ? 'Drag one onto a shape, or select a shape and click.'
              : blocked
                ? `${target.name} brings its own materials — drop onto a shape instead.`
                : `Click applies to ${target.name}.`}
          </Typography>
          {target?.kind === 'primitive' && target.material && (
            <Button
              size="small"
              onClick={() => dispatch([{ op: 'set_material', node_ids: [target.id], slug: null }])}
              sx={{ textTransform: 'none' }}
            >
              Clear
            </Button>
          )}
        </Box>
      )}

      <AssetGrid
        assets={tiles}
        loading={loading || (wantsSketchfab && sketchfab.loading && assets.length === 0)}
        error={error ?? (assets.length === 0 ? sketchfab.error : undefined)}
        onPick={pick}
        pack={type === 'models' || type === 'textures' ? pack : undefined}
      />
    </>
  );
};

/**
 * Where everything in a scene comes from.
 *
 * The library and the outliner share this slot (devplan §5.2) because both want
 * the same width and only one is wanted at a time; the library opens because it
 * is the one a user reaches for on an empty scene.
 */
export const LibraryPanel = ({
  doc,
  selection,
  mode,
  onModeChange,
  tab,
  onTabChange,
  onSelect,
  dispatch,
}: LibraryPanelProps) => {
  const [open, setOpen] = useState(true);

  if (!open) {
    return <CollapsedRail side="left" label="Assets" onExpand={() => setOpen(true)} />;
  }

  return (
    <Box
      className="flex h-full w-80 shrink-0 flex-col border-r"
      sx={{ bgcolor: 'background.paper', borderColor: 'divider' }}
    >
      <Box className="flex items-center justify-between gap-1 px-3 pt-3">
        <ToggleButtonGroup
          size="small"
          value={mode}
          exclusive
          onChange={(_event, next: PanelMode | null) => next && onModeChange(next)}
        >
          <ToggleButton value="library" className="px-2 py-0.5 text-xs">
            Library
          </ToggleButton>
          <ToggleButton value="outliner" className="px-2 py-0.5 text-xs">
            Outliner
          </ToggleButton>
        </ToggleButtonGroup>
        <Tooltip title="Hide assets">
          <IconButton size="small" aria-label="Hide assets" onClick={() => setOpen(false)}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {mode === 'outliner' ? (
        <Outliner doc={doc} selection={selection} onSelect={onSelect} />
      ) : (
        <>
          <Tabs
            value={tab}
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
            onChange={(_event, next: LibraryTab) => onTabChange(next)}
            className="min-h-0 border-b"
            sx={{ borderColor: 'divider', '& .MuiTabs-scrollButtons.Mui-disabled': { opacity: 0.3 } }}
          >
            {TABS.map((item) => (
              <Tab key={item.id} value={item.id} label={item.label} className="min-h-10 px-2 text-xs" />
            ))}
          </Tabs>

          {isAssetType(tab) ? (
            // Keyed so switching tabs starts a clean catalogue rather than
            // showing the previous one filtered by the new one's chips.
            <CatalogTab
              key={tab}
              type={tab}
              target={doc.nodes.find((node) => node.id === selection[0])}
              dispatch={dispatch}
            />
          ) : tab === 'primitives' ? (
            <Box className="flex flex-1 flex-col gap-2 p-3">
              <Box className="grid grid-cols-3 gap-2">
                {PRIMITIVE_TYPES.map((type) => {
                  const Icon = PRIMITIVE_ICONS[type];
                  return (
                    <Button
                      key={type}
                      size="small"
                      variant="outlined"
                      onClick={() => dispatch([{ op: 'add_primitive', type }])}
                      className="flex-col gap-1 py-2"
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.7rem',
                        lineHeight: 1.2,
                        color: 'text.secondary',
                        borderColor: 'divider',
                        '&:hover': { color: 'text.primary', borderColor: 'text.secondary' },
                      }}
                    >
                      <Icon fontSize="medium" />
                      {LABELS[type]}
                    </Button>
                  );
                })}
              </Box>
              <Typography variant="caption" color="text.secondary">
                What a scene gets blocked out with before anything real arrives.
              </Typography>
            </Box>
          ) : tab === 'lighting' ? (
            <Box className="flex flex-1 flex-col items-start gap-2 p-3">
              {LIGHT_TYPES.map((type) => (
                <Button
                  key={type}
                  size="small"
                  variant="outlined"
                  fullWidth
                  onClick={() => dispatch([{ op: 'add_light', type }])}
                >
                  {LABELS[type]} light
                </Button>
              ))}
              <Typography variant="caption" color="text.secondary">
                Lights are ours — Poly Haven ships none. An HDRI lights a scene on its own.
              </Typography>
            </Box>
          ) : (
            <Box className="flex flex-1 items-center px-4">
              <Typography variant="body2" color="text.secondary">
                Your uploads live here. They arrive with P5.
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Required while we use the live APIs, and cheap: a named credit where
          the content appears (devplan §5.6, Sketchfab's own guidelines). */}
      <Box className="border-t px-3 py-2" sx={{ borderColor: 'divider' }}>
        <Tooltip title="Assets from Poly Haven (CC0) and Sketchfab">
          <Typography variant="caption" color="text.secondary">
            Powered by{' '}
            <Link href="https://polyhaven.com" target="_blank" rel="noreferrer noopener" color="inherit">
              Poly Haven
            </Link>{' '}
            and{' '}
            <Link href="https://sketchfab.com" target="_blank" rel="noreferrer noopener" color="inherit">
              Sketchfab
            </Link>
          </Typography>
        </Tooltip>
      </Box>
    </Box>
  );
};
